import type { ShopifyCart, ShopifyCartItem } from '~/types/shopify'

const FREE_SHIP_THRESHOLD = 15000 // cents ($150)
const FOCUSABLE_SEL = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function initCartDrawer(): void {
  const drawer = document.querySelector<HTMLElement>('[data-cart-drawer]')
  if (!drawer) return

  const scrim = drawer.querySelector<HTMLElement>('[data-cart-scrim]')
  const closers = drawer.querySelectorAll<HTMLElement>('[data-cart-close]')
  const panel = drawer.querySelector<HTMLElement>('aside')
  const bodyEmpty = drawer.querySelector<HTMLElement>('[data-cart-empty]')
  const itemsList = drawer.querySelector<HTMLElement>('[data-cart-items]')
  const shipWrap = drawer.querySelector<HTMLElement>('[data-cart-ship]')
  const shipFill = drawer.querySelector<HTMLElement>('[data-cart-ship-fill]')
  const shipProgress = drawer.querySelector<HTMLElement>('[data-cart-ship-progress]')
  const shipLabel = drawer.querySelector<HTMLElement>('[data-cart-ship-label]')
  const subtotal = drawer.querySelector<HTMLElement>('[data-cart-subtotal]')
  const countEl = drawer.querySelector<HTMLElement>('[data-cart-drawer-count]')
  const liveEl = drawer.querySelector<HTMLElement>('[data-cart-live]')
  let lastFocused: HTMLElement | null = null

  function announce(msg: string): void {
    if (!liveEl) return
    // Reset to force SR re-announcement
    liveEl.textContent = ''
    window.setTimeout(() => { liveEl.textContent = msg }, 50)
  }

  function setToggleState(expanded: boolean): void {
    document.querySelectorAll<HTMLElement>('[data-cart-toggle]').forEach((b) => {
      b.setAttribute('aria-expanded', String(expanded))
    })
  }

  function open(): void {
    lastFocused = document.activeElement as HTMLElement | null
    drawer!.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'
    setToggleState(true)
    panel?.focus()
    refresh()
  }
  function close(): void {
    drawer!.setAttribute('aria-hidden', 'true')
    document.body.style.overflow = ''
    setToggleState(false)
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus()
    }
  }

  function trapFocus(e: KeyboardEvent): void {
    if (e.key !== 'Tab' || drawer!.getAttribute('aria-hidden') === 'true' || !panel) return
    const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SEL))
      .filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null)
    if (focusables.length === 0) return
    const first = focusables[0]!
    const last = focusables[focusables.length - 1]!
    const active = document.activeElement as HTMLElement | null
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  async function refresh(): Promise<void> {
    try {
      const res = await fetch('/cart.js')
      const cart = await res.json() as ShopifyCart
      render(cart)
      window.dispatchEvent(new CustomEvent('meera:cart:updated', { detail: { itemCount: cart.item_count, total: cart.total_price } }))
    } catch (err) {
      console.error('Cart refresh failed', err)
    }
  }

  function render(cart: ShopifyCart): void {
    if (countEl) countEl.textContent = `(${cart.item_count})`
    // Update header cart-count text + sr-only label
    const headerCount = document.querySelector<HTMLElement>('[data-cart-count]')
    if (headerCount) headerCount.textContent = String(cart.item_count)
    const headerCountLabel = document.querySelector<HTMLElement>('[data-cart-count-label]')
    if (headerCountLabel) headerCountLabel.textContent = `${cart.item_count} item${cart.item_count === 1 ? '' : 's'} in cart`

    if (cart.items.length === 0) {
      bodyEmpty!.hidden = false
      itemsList!.hidden = true
      shipWrap!.hidden = true
    } else {
      bodyEmpty!.hidden = true
      itemsList!.hidden = false
      shipWrap!.hidden = false
      itemsList!.innerHTML = cart.items.map(renderItem).join('')
      const pct = Math.min(100, (cart.total_price / FREE_SHIP_THRESHOLD) * 100)
      shipFill!.style.width = `${pct}%`
      if (shipProgress) shipProgress.setAttribute('aria-valuenow', String(Math.round(pct)))
      if (cart.total_price >= FREE_SHIP_THRESHOLD) {
        shipLabel!.textContent = 'Free shipping unlocked'
      } else {
        const away = (FREE_SHIP_THRESHOLD - cart.total_price) / 100
        shipLabel!.textContent = `$${away.toFixed(2)} away from free shipping`
      }
    }
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price)
    wireLineActions()
    announce(`Cart updated. ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}, subtotal ${formatMoney(cart.total_price)}.`)
  }

  function renderItem(item: ShopifyCartItem): string {
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<div class="cart-drawer__item-variant">${esc(item.variant_title)}</div>`
      : ''
    return `
      <li class="cart-drawer__item" data-line-item data-key="${item.key}">
        <div class="cart-drawer__item-img">${item.image ? `<img src="${item.image}" alt="${esc(item.product_title)}" width="80" height="80" loading="lazy" decoding="async">` : ''}</div>
        <div class="cart-drawer__item-body">
          <div class="cart-drawer__item-row">
            <h3 class="cart-drawer__item-title">${esc(item.product_title)}</h3>
            <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
          </div>
          ${variant}
          <div class="cart-drawer__item-actions">
            <div class="cart-drawer__item-qty" role="group" aria-label="Quantity for ${esc(item.product_title)}">
              <button type="button" data-qty-dec aria-label="Decrease quantity of ${esc(item.product_title)}">−</button>
              <input type="text" readonly value="${item.quantity}" data-qty-input aria-label="Quantity">
              <button type="button" data-qty-inc aria-label="Increase quantity of ${esc(item.product_title)}">+</button>
            </div>
            <button type="button" class="cart-drawer__item-remove" data-line-remove aria-label="Remove ${esc(item.product_title)} from cart">Remove</button>
          </div>
        </div>
      </li>
    `
  }

  function wireLineActions(): void {
    drawer!.querySelectorAll<HTMLElement>('[data-line-item]').forEach((row) => {
      const key = row.dataset.key
      if (!key) return
      const qtyInput = row.querySelector<HTMLInputElement>('[data-qty-input]')
      row.querySelector('[data-qty-inc]')?.addEventListener('click', () => updateQty(key, Number(qtyInput?.value ?? 1) + 1))
      row.querySelector('[data-qty-dec]')?.addEventListener('click', () => updateQty(key, Math.max(0, Number(qtyInput?.value ?? 1) - 1)))
      row.querySelector('[data-line-remove]')?.addEventListener('click', () => updateQty(key, 0))
    })
  }

  async function updateQty(key: string, qty: number): Promise<void> {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    })
    await refresh()
  }

  window.addEventListener('meera:cart:open', open)
  window.addEventListener('meera:cart:close', close)
  window.addEventListener('meera:cart:refresh', refresh)
  scrim?.addEventListener('click', close)
  closers.forEach((btn) => btn.addEventListener('click', close))
  document.addEventListener('keydown', (e) => {
    if (drawer!.getAttribute('aria-hidden') === 'true') return
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'Tab') {
      trapFocus(e)
    }
  })

  refresh()
}

function formatMoney(cents: number): string {
  return '$' + (cents / 100).toFixed(2)
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!))
}
